import Flex from 'common/components/Flex'
import HeaderLogo from 'common/components/Header/HeaderLogo'
import HeaderUserButtonGroup from 'common/components/Header/HeaderUserButtonGroup'
import Loader from 'common/components/Loader'
import Overlay from 'common/components/Overlay'
import SearchBar from 'common/components/SearchBar'
import TopNav from 'common/components/TopNav'
import {
  ACTIVE_THEME_NAME,
  WEB_DESKTOP_RALALI_URL,
} from 'common/constants'
import theme from 'common/theme'
import { getCookie } from 'lib/cookie'
import { useFeatureFlags } from 'lib/feature-flags'
import ThemeColorHelper from 'lib/theme-color-helper'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const ModalSuspendedRalaliPlus = dynamic(
  () =>
    import('services/RalaliPlus/widgets/ModalSuspendedRalaliPlus'),
  { ssr: false },
)

const Container = styled.div`
  background-color: ${ThemeColorHelper('white')};
  background-image: ${theme.backgroundImage[ACTIVE_THEME_NAME]};
  background-position: top;
  background-repeat: no-repeat;
  background-size: 100%;
  position: sticky;
  top: 0;
  z-index: ${theme.zIndex.header};
  ${({ hideTopNav }) =>
    hideTopNav &&
    `
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.08);
  `}
`

const SearchBarWrapper = styled.div`
  width: ${theme.layout.containerWidth};
  height: 90px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
`

const StyledSearchBar = styled(SearchBar)`
  margin-left: 30px;
  margin-right: 14px;
  > div > * + div {
    background-color: ${ThemeColorHelper('orange')};
    border: 1px solid
      ${ACTIVE_THEME_NAME
        ? theme.colors.custom.white
        : theme.colors.primary};
    border-left: none;
  }
`

const LoaderWrapper = styled(Flex)`
  height: 100vh;
`

function Header({
  hideTopNav = false,
}: {
  hideTopNav?: boolean
}): JSX.Element {
  // FEATURE FLAGS
  const isFeatureSetAndChangePinEnable = useFeatureFlags(
    'biz--ralali-pin--enabled-set-and-change-pin--global',
  )

  const isMarketplaceCartEnabled = useFeatureFlags(
    'biz--marketplace-enable-cart--global',
  )

  const { isLoading: isLoadingLogout } = useStoreState(
    (actions) => actions.logOut,
  )
  const {
    isLoading: isLoadingRalaliPlus,
    balanceData,
  } = useStoreState((state) => state.ralaliPlus)
  const { getTotalUnreadMessages } = useStoreActions(
    (actions) => actions.userChat,
  )
  const isLogin =
    !!getCookie('access_token') || !!getCookie('refresh_token')
  const [isOpenOverlay, setIsOpenOverlay] = useState<boolean>(false)
  const handleOpenOverlay = () => {
    setIsOpenOverlay(true)
  }

  const handleCloseOverlay = () => {
    setIsOpenOverlay(false)
  }

  const [isOpenRalaliPlusModal, setIsOpenRalaliPlusModal] = useState(
    false,
  )
  const [
    isLoadedRalaliPlusModal,
    setIsLoadedRalaliPlusModal,
  ] = useState(false)

  const [
    isLoadedSuspendedRalaliPlusModal,
    setIsLoadedSuspendedRalaliPlusModal,
  ] = useState(false)

  const handleOpenRalaliPlusModal = () => {
    if (
      !isLoadedRalaliPlusModal &&
      !isLoadingRalaliPlus &&
      balanceData?.status === 'suspended'
    ) {
      setIsLoadedSuspendedRalaliPlusModal(true)
    } else {
      setIsLoadedRalaliPlusModal(true)
    }
    setIsOpenRalaliPlusModal(!isOpenRalaliPlusModal)
  }

  useEffect(() => {
    getTotalUnreadMessages('marketplace')
  }, [])

  return (
    <>
      <Overlay
        isActive={isOpenOverlay}
        onOverlayClick={handleCloseOverlay}
        zIndex="6"
      />
      {isLoadingLogout && (
        <Overlay
          zIndex={`${theme.zIndex.modal}`}
          isActive={isLoadingLogout}
        >
          <LoaderWrapper
            data-testid="loading-logout-overlay"
            alignItems="center"
            justifyContent="center"
          >
            <Loader />
          </LoaderWrapper>
        </Overlay>
      )}
      <Container data-testid="header" hideTopNav={hideTopNav}>
        <SearchBarWrapper>
          <Link href={`${WEB_DESKTOP_RALALI_URL}`}>
            <a>
              <HeaderLogo />
            </a>
          </Link>

          {/* TODO:: replace next line with Search Component */}
          <StyledSearchBar
            // lastSeenItems={lastSeenDummy}
            // popularCategories={lastSeenDummy}
            onOpenOverlay={handleOpenOverlay}
            onCloseOverlay={handleCloseOverlay}
            width="100%"
          />

          <HeaderUserButtonGroup
            onCloseOverlay={handleCloseOverlay}
            onOpenOverlay={handleOpenOverlay}
            onOpenRalaliPlusModal={handleOpenRalaliPlusModal}
            isFeatureSetAndChangePinEnable={
              isFeatureSetAndChangePinEnable
            }
            isLogin={isLogin}
            isMarketplaceCartEnabled={isMarketplaceCartEnabled}
          />
        </SearchBarWrapper>

        <TopNav
          onOpenOverlay={handleOpenOverlay}
          onCloseOverlay={handleCloseOverlay}
          show={!hideTopNav}
        />
      </Container>
      {isLoadedSuspendedRalaliPlusModal ? (
        <ModalSuspendedRalaliPlus
          isOpen={isOpenRalaliPlusModal}
          onClose={handleOpenRalaliPlusModal}
        />
      ) : (
        ''
      )}
    </>
  )
}

export default Header
